import { useContext, useEffect } from "react";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate
} from "react-router-dom";
import classes from "./App.module.scss";
import CircleButton from "./components/CircleButton/CircleButton";
import Form from "./components/Form";
import LoadingScreen from "./components/LoadingScreen/LoadingScreen";
import Navbar from "./components/Navbar";
import Toast from "./components/Toast";
import { APIContext, APIProvider } from "./connector";
import { maintenance_mode } from "./constants";
import { ToastProvider } from "./context/ToastContext";
import Booking from "./pages/clientside/Booking";
import Comingsoon from "./pages/common/Comingsoon";
import DriverFound from "./pages/clientside/DriverFound";
import SearchingDriver from "./pages/clientside/SearchingDriver";
import ChatPage from "./pages/common/Chat";
import Home from "./pages/common/Home";
import Login from "./pages/common/Login";
import Maintenance from "./pages/common/Maintenance";
import Profile from "./pages/common/Profile";
import Register from "./pages/common/Register";
import TripHistory from "./pages/common/TripHistory";
import CurrentOrders from "./pages/driverside/CurrentOrders";
import DriverCheckIn from "./pages/driverside/DriverCheckIn";
import DriverOrders from "./pages/driverside/DriverOrders";
import DriverRegister from "./pages/driverside/DriverRegister";
import Waiting from "./pages/driverside/Waiting";
import { getRelativePath } from "./utils/helpers";


const refresh = () => {
  window.location.reload();
};

// Main App component
function App() {
  // determine basename so the app works when mounted at a subpath
  const getBasename = () => {
    // prefer explicit runtime/build-time env var
    const envBase = process.env.PUBLIC_URL;
    if (envBase) return envBase.replace(/\/$/, "");

    // runtime fallback: if the app is being served under /staging, use that
    if (typeof window !== "undefined") {
      const path = window.location.pathname || "";
      if (path.startsWith("/staging")) return "/staging";
    }

    // default (root)
    return undefined;
  };

  return (
    <BrowserRouter basename={getBasename()}>
      <APIProvider>
        <ToastProvider>
          <AppContent />
          <Toast />
        </ToastProvider>
      </APIProvider>
    </BrowserRouter>
  );
}

// Component that uses the context - must be inside APIProvider
const AppContent = () => {
  const { activeTripData, loading, userData } = useContext(APIContext);
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;
  const isChat = path.startsWith("/chat");

  const comingSoonMode = userData?.zone?.coming_soon === true;

  useEffect(() => {
    if (loading || !activeTripData?.trip_id || !userData?.user_type) return;

    const tripId = activeTripData.trip_id;
    const userType = userData.user_type;

    // Define allowed paths during an active trip
    const isAllowedPath =
      path.startsWith("/chat") ||
      path.startsWith("/profile/") ||
      (userType === "CLIENT" &&
        (path === `/searching-driver/${tripId}` || path === `/driver-found/${tripId}`)) ||
      (userType === "DRIVER" && path === `/currentOrders/${tripId}`);

    // if attempting to navigate to unallowed path, redirect to appropriate path
    if (!isAllowedPath) {
      if (userType === "CLIENT") {
        const targetPath =
          activeTripData.status === "PENDING_OFFER"
            ? `/searching-driver/${tripId}`
            : `/driver-found/${tripId}`;
        navigate(targetPath, { replace: true });
      } else if (userType === "DRIVER") {
        navigate(`/currentOrders/${tripId}`, { replace: true });
      }
    }
  }, [activeTripData, userData, loading, path, navigate]);

  if (loading) return <LoadingScreen />;

  if (maintenance_mode && !activeTripData?.trip_id) {
    return (
      <Form>
        <Routes>
          {/* public routes */}
          <Route element={<PublicRoute />}>
            <Route index path="/" element={<Maintenance />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
          </Route>

          {/* driver routes */}
          <Route element={<DriverRoute />}>
            <Route path="/waiting" element={<Waiting />} />
          </Route>

          {/* admin routes */}
          <Route element={<AdminRoute />}></Route>

          {/* shared routes (client and driver) */}
          <Route element={<SharedRoute />}>
            <Route path="/driverRegister" element={<DriverRegister />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <CircleButton
          className={`${classes.refreshButton} ${isChat ? classes.refreshButtonChat : ""}`}
          onClick={refresh}
        >
          ↻
        </CircleButton>

      </Form>
    );
  }

  if (comingSoonMode && !activeTripData?.trip_id) {
    return (
      <Form>
        <Routes>
          {/* public routes */}
          <Route element={<PublicRoute />}>
            <Route index path="/" element={<Comingsoon />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
          </Route>

          {/* driver routes */}
          <Route element={<DriverRoute />}>
            <Route path="/waiting" element={<Waiting />} />
          </Route>

          {/* admin routes */}
          <Route element={<AdminRoute />}></Route>

          {/* shared routes (client and driver) */}
          <Route element={<SharedRoute />}>
            <Route path="/driverRegister" element={<DriverRegister />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <CircleButton
          className={`${classes.refreshButton} ${isChat ? classes.refreshButtonChat : ""}`}
          onClick={refresh}
        >
          ↻
        </CircleButton>

      </Form>
    );
  }

  return (
    <div>
      {/* <img src={logo} alt="" className="header-logo" /> */}
      <Form>
        <Navbar />
        <CircleButton
          className={`${classes.refreshButton} ${isChat ? classes.refreshButtonChat : ""}`}
          onClick={refresh}
        >
          ↻
        </CircleButton>
        <Routes>
          {/* public routes */}
          <Route element={<PublicRoute />}>
            <Route index path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
          </Route>

          {/* client routes */}
          <Route element={<ClientRoute />}>
            <Route path="/booking" element={<Booking />} />
            <Route
              path="/searching-driver/:tripId"
              element={<SearchingDriver />}
            />
            <Route path="/driver-found/:tripId" element={<DriverFound />} />
          </Route>

          {/* driver routes */}
          <Route element={<DriverRoute />}>
            <Route path="/driver" element={<DriverCheckIn />} />
            <Route path="/driver-orders" element={<DriverOrders />} />
            <Route path="/waiting" element={<Waiting />} />
            <Route path="/currentOrders/:tripId" element={<CurrentOrders />} />
          </Route>

          {/* shared routes (client and driver) */}
          <Route element={<SharedRoute />}>
            {/* <Route path="/send-order" element={<DriverSendOrder />} /> */}
            <Route path="/driverRegister" element={<DriverRegister />} />
            <Route path="/profile/:userId" element={<Profile />} />
            <Route path="/trip-history" element={<TripHistory />} />
            <Route path="/chat" element={<ChatPage />} />
          </Route>

          {/* admin routes */}
          <Route element={<AdminRoute />}></Route>

          <Route path="*" element={<h1>Page Not Found</h1>} />
        </Routes>
      </Form>
    </div>
  );
};

const getRedirectPath = (userData) => {
  if (!userData || Object.keys(userData).length === 0) {
    return "/login";
  }

  const { user_type, driver_info, zone } = userData;
  const comingSoonMode = zone?.coming_soon === true;
  const isRestrictedMode = maintenance_mode || comingSoonMode;

  switch (user_type) {
    case "CLIENT":
      return isRestrictedMode ? "/" : "/booking";

    case "DRIVER":
      if (driver_info?.is_approved_driver === true) {
        return isRestrictedMode ? "/" : "/driver";
      }
      if (driver_info?.is_approved_driver === undefined) {
        return "/driverRegister";
      }
      return driver_info?.is_approved_driver === false
        ? "/waiting"
        : "/driverRegister";

    case "ADMIN":
      return "/driverList";

    default:
      return "/";
  }
};

const ClientRoute = () => {
  const { userData } = useContext(APIContext);

  // if no user data, redirect to login
  if (!userData || Object.keys(userData).length === 0)
    return <Navigate to="/login" replace />;

  // if user_type is CLIENT, allow access
  if (userData?.user_type === "CLIENT") {
    return <Outlet />;
  }

  // else redirect to appropriate page
  return <Navigate to={getRedirectPath(userData)} />;
};

const DriverRoute = () => {
  const { userData } = useContext(APIContext);
  const driverPaths = [
    "/driver",
    "/driver-orders",
    "/waiting",
    "/currentOrders/",
    "/send-order",
    "/trip-history",
  ];

  // if no user data, redirect to login
  if (!userData || Object.keys(userData).length === 0)
    return <Navigate to={getRedirectPath(userData)} replace />;

  const path = getRelativePath();
  const comingSoonMode = userData?.zone?.coming_soon === true;
  const isRestrictedMode = maintenance_mode || comingSoonMode;

  if (!driverPaths.some((p) => path.startsWith(p)))
    return <Navigate to={getRedirectPath(userData)} replace />;

  if (userData?.user_type !== "DRIVER")
    return <Navigate to={getRedirectPath(userData)} replace />;

  const approved = userData?.driver_info?.is_approved_driver;

  if (isRestrictedMode) {
    if (approved === false && path === "/waiting") return <Outlet />;
    return <Navigate to={getRedirectPath(userData)} replace />;
  }

  if (approved === false && path === "/waiting") return <Outlet />;
  if (
    approved === true &&
    (["/driver", "/driver-orders", "/send-order", "/profile", "/chat", "/trip-history"].includes(
      path
    ) ||
      /^\/currentOrders\/[^/]+$/.test(path))
  )
    return <Outlet />;

  return <Navigate to={getRedirectPath(userData)} replace />;
};

const AdminRoute = () => {
  const { userData } = useContext(APIContext);

  // if user_type is ADMIN, allow access
  if (userData?.user_type === "ADMIN") {
    return <Outlet />;
  }

  // else redirect to appropriate page
  return <Navigate to={getRedirectPath(userData)} />;
};

const PublicRoute = () => {
  const { userData } = useContext(APIContext);
  const publicPaths = ["/", "/login", "/register"];

  const path = getRelativePath();
  const comingSoonMode = userData?.zone?.coming_soon === true;
  const isRestrictedMode = maintenance_mode || comingSoonMode;

  // if not in public paths, redirect to appropriate page
  if (!publicPaths.includes(path))
    return <Navigate to={getRedirectPath(userData)} replace />;
  // let user in if on login or register page and not logged in
  if (["/login", "/register"].includes(path)) {
    return !userData || Object.keys(userData).length === 0 ? (
      <Outlet />
    ) : (
      <Navigate to={getRedirectPath(userData)} replace />
    );
  }
  // if not logged in, just redirect to appropriate page
  if (!userData || Object.keys(userData).length === 0)
    return <Navigate to={getRedirectPath(userData)} replace />;
  // if in maintenance mode or coming soon mode, force redirect to home page
  if (isRestrictedMode && path !== "/")
    return <Navigate to={getRedirectPath(userData)} replace />;

  return <Outlet />;
};

const SharedRoute = () => {
  const { userData } = useContext(APIContext);
  const sharedPaths = ["/driverRegister", "/profile", "/chat", "/trip-history"];

  const path = getRelativePath();
  const comingSoonMode = userData?.zone?.coming_soon === true;
  const isRestrictedMode = maintenance_mode || comingSoonMode;

  // if no user data, redirect to login
  if (!userData || Object.keys(userData).length === 0)
    return <Navigate to={getRedirectPath(userData)} replace />;

  if (!sharedPaths.some((p) => path.startsWith(p)))
    return <Navigate to={getRedirectPath(userData)} replace />;

  if (isRestrictedMode) {
    if (
      userData?.driver_info?.is_approved_driver === undefined &&
      path === "/driverRegister"
    )
      return <Outlet />;
    return <Navigate to={getRedirectPath(userData)} replace />;
  }

  if (userData?.user_type === "CLIENT") return <Outlet />;

  if (userData?.user_type === "DRIVER") {
    const approved = userData?.driver_info?.is_approved_driver;

    if (approved === false && path === "/waiting") return <Outlet />;
    if (approved === undefined && path === "/driverRegister") return <Outlet />;
    if (
      approved === true &&
      (["/driver", "/driver-orders", "/send-order", "/chat", "/trip-history"].includes(path) ||
        /^\/profile\/[^/]+$/.test(path) ||
        /^\/currentOrders\/[^/]+$/.test(path))
    )
      return <Outlet />;
  }

  return <Navigate to={getRedirectPath(userData)} replace />;
};

export default App;
